(() => {
  const sentinelSelector = '[data-ffa-infinite-scroll]';

  /**
   * Runs only inline scripts inside newly appended product cards.
   * External theme assets are not loaded repeatedly.
   */
  function executeInlineScripts(container) {
    const scripts = container.querySelectorAll(
      'script:not([src])'
    );

    scripts.forEach((oldScript) => {
      const scriptType =
        oldScript.getAttribute('type') || '';

      const executableTypes = [
        '',
        'text/javascript',
        'application/javascript',
        'module'
      ];

      if (!executableTypes.includes(scriptType)) {
        return;
      }

      const newScript = document.createElement('script');

      Array.from(oldScript.attributes).forEach(
        (attribute) => {
          newScript.setAttribute(
            attribute.name,
            attribute.value
          );
        }
      );

      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  function initialiseInfiniteScroll(sentinel) {
    if (
      !sentinel ||
      sentinel.dataset.ffaInfiniteInitialised === 'true'
    ) {
      return;
    }

    const collectionComponent = sentinel.closest(
      'collection-component'
    );

    const productGrid =
      collectionComponent?.querySelector('#product-grid');

    if (!collectionComponent || !productGrid) {
      return;
    }

    sentinel.dataset.ffaInfiniteInitialised = 'true';

    const sectionId =
      productGrid.dataset.id || '';

    const spinner = sentinel.querySelector(
      '[data-ffa-infinite-spinner]'
    );

    const status = sentinel.querySelector(
      '[data-ffa-infinite-status]'
    );

    let isLoading = false;
    let observer;

    collectionComponent.classList.add(
      'ffa-infinite-scroll-ready'
    );

    function showSpinner() {
      if (spinner) {
        spinner.hidden = false;
      }

      if (status) {
        status.textContent = 'Loading more products...';
        status.classList.remove('is-error');
      }
    }

    function hideSpinner() {
      if (spinner) {
        spinner.hidden = true;
      }
    }

    function showCompletedMessage() {
      isLoading = false;
      hideSpinner();

      if (status) {
        status.textContent =
          'You have viewed all products.';

        status.classList.remove('is-error');
      }

      observer?.disconnect();
    }

    function showErrorMessage() {
      isLoading = false;
      hideSpinner();

      if (status) {
        status.textContent =
          'More products could not be loaded automatically.';

        status.classList.add('is-error');
      }

      /*
       * Restore standard pagination if infinite
       * scrolling cannot load the next page.
       */
      collectionComponent.classList.remove(
        'ffa-infinite-scroll-ready'
      );

      observer?.disconnect();
    }

    function createSectionRequestUrl(nextUrl) {
      const url = new URL(
        nextUrl,
        window.location.origin
      );

      /*
       * Shopify Section Rendering API:
       * fetch only this collection section instead
       * of downloading the complete page.
       */
      if (sectionId) {
        url.searchParams.set(
          'section_id',
          sectionId
        );
      }

      return url.toString();
    }

    async function loadNextPage() {
      if (isLoading) {
        return;
      }

      const nextUrl = sentinel.dataset.nextUrl;

      if (!nextUrl) {
        showCompletedMessage();
        return;
      }

      isLoading = true;
      showSpinner();

      try {
        const response = await fetch(
          createSectionRequestUrl(nextUrl),
          {
            method: 'GET',
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest'
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            `Request failed: ${response.status}`
          );
        }

        const html = await response.text();

        const parsedDocument =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        const nextProductGrid =
          parsedDocument.querySelector(
            '#product-grid'
          );

        if (!nextProductGrid) {
          throw new Error(
            'Next product grid was not found.'
          );
        }

        const nextProductItems = Array.from(
          nextProductGrid.children
        );

        if (!nextProductItems.length) {
          showCompletedMessage();
          return;
        }

        /*
         * Append only product cards.
         * Filters, sorting and the collection wrapper
         * are never replaced or re-rendered.
         */
        nextProductItems.forEach((productItem) => {
          const importedItem = document.importNode(
            productItem,
            true
          );

          productGrid.appendChild(importedItem);
          executeInlineScripts(importedItem);
        });

        const nextSentinel =
          parsedDocument.querySelector(
            sentinelSelector
          );

        sentinel.dataset.nextUrl =
          nextSentinel?.dataset.nextUrl || '';

        /*
         * Update fallback pagination without replacing
         * any filters or collection controls.
         */
        const currentPagination =
          collectionComponent.querySelector(
            '.ffa-infinite-pagination'
          );

        const nextPagination =
          parsedDocument.querySelector(
            '.ffa-infinite-pagination'
          );

        if (currentPagination) {
          currentPagination.innerHTML =
            nextPagination?.innerHTML || '';
        }

        document.dispatchEvent(
          new CustomEvent(
            'ffa:products:appended',
            {
              detail: {
                count: nextProductItems.length
              }
            }
          )
        );

        if (!sentinel.dataset.nextUrl) {
          showCompletedMessage();
          return;
        }

        isLoading = false;
        hideSpinner();

        if (status) {
          status.textContent = '';
          status.classList.remove('is-error');
        }
      } catch (error) {
        console.error(
          'Furniture For America infinite scroll:',
          error
        );

        showErrorMessage();
      }
    }

    observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some(
          (entry) => entry.isIntersecting
        );

        if (isVisible && !isLoading) {
          loadNextPage();
        }
      },
      {
        root: null,
        rootMargin: '400px 0px',
        threshold: 0
      }
    );

    observer.observe(sentinel);
  }

  function initialiseWithinElement(element) {
    if (!(element instanceof Element)) {
      return;
    }

    if (element.matches(sentinelSelector)) {
      initialiseInfiniteScroll(element);
    }

    element
      .querySelectorAll(sentinelSelector)
      .forEach(initialiseInfiniteScroll);
  }

  function initialisePage() {
    document
      .querySelectorAll(sentinelSelector)
      .forEach(initialiseInfiniteScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialisePage,
      { once: true }
    );
  } else {
    initialisePage();
  }

  /*
   * This observer only checks newly added elements for
   * a new infinite-scroll sentinel.
   *
   * Product cards appended to the grid do not contain
   * the sentinel, so they do not trigger reinitialisation
   * or filter rendering.
   */
  const renderObserver = new MutationObserver(
    (mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          initialiseWithinElement(node);
        });
      });
    }
  );

  renderObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseWithinElement(event.target);
    }
  );
})();